import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import Group from "lib/api/Group";
import Company from "lib/api/Company";
import { InfiniteLoader, AutoSizer, Column, Table } from "react-virtualized";
import "react-virtualized/styles.css";
import PageLayout from "components/layouts/PageLayout";
import Button from "components/common/Button";
import ButtonGroup from "components/common/ButtonGroup";
import EntityTypeahead from "components/common/EntityTypeahead";
import { filterBarStyles } from "components/common/FilterBar/styles";
import { CompanyContext } from "lib/context/CompanyContext";
import { XMarkIcon } from '@heroicons/react/24/outline';

const GROUP_LIST_STATE_KEY = "group_list_view_state";

function loadPersistedViewState() {
  try {
    const stored = sessionStorage.getItem(GROUP_LIST_STATE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    // ignore malformed/missing state
  }
  return null;
}

export default function GroupList(props) {
  const { company } = useContext(CompanyContext);
  const persistedViewState = loadPersistedViewState();

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(1);
  const [search, setSearch] = useState(persistedViewState?.search || "");
  const [loading, setLoading] = useState(false);
  const [companyFilter, setCompanyFilter] = useState(persistedViewState?.companyFilter || null);
  const hasNextPage = data.length < total;

  const [recordsPerPage, setRecordsPerPage] = useState(100);

  const effectiveCompanyUuid = companyFilter?.uuid || company.uuid;

  async function loadNextPage({ startIndex, stopIndex }) {
    if (loading) return;
    await setLoading(true);

    let params = {};
    if(effectiveCompanyUuid || search){
      if(effectiveCompanyUuid){
        params["company_uuid"] = effectiveCompanyUuid;
      }
      if(search){
        params["search"] = search;
      }
    }else{
      params = false;
    }
    let response = await Group.list(startIndex, Math.max(recordsPerPage, stopIndex - startIndex + 1), params);
    if (response.total !== total) {
      await setTotal(response.total);
    }
    const newData = response.data;
    setData(prevRecords => [...prevRecords, ...newData]);
    await setLoading(false);
  }

  async function load() {
    let params = {};
    if(effectiveCompanyUuid || search){
      if(effectiveCompanyUuid){
        params["company_uuid"] = effectiveCompanyUuid;
      }
      if(search){
        params["search"] = search;
      }
    }else{
      params = false;
    }
    await setLoading(true);
    let results = await Group.list(0, recordsPerPage, params);
    await setTotal(results.total);
    await setData(results.data);
    await setLoading(false);
  }
  const loadMoreRows = loading ? () => {} : loadNextPage;
  const isRowLoaded = ({ index }) => !hasNextPage || index < data.length;

  useEffect(() => {
    load();
    return ()=>{setLoading(false)};
  }, [company, companyFilter]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        GROUP_LIST_STATE_KEY,
        JSON.stringify({ search, companyFilter })
      );
    } catch (error) {
      // ignore storage write failures (e.g. quota exceeded)
    }
  }, [search, companyFilter]);

  function clearFilters() {
    setSearch("");
    setCompanyFilter(null);
  }
  return (
    <PageLayout>
    <style>{filterBarStyles}</style>
    <h1>Groups</h1>
    <div className="hr hr--darkest"></div>

    <div className="filter-bar">
      <div className="filter-bar__field">
        <span className="filter-bar__label">Search</span>
        <input
          className="filter-bar__input"
          placeholder="Search by name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === "Enter" && load()}
        />
      </div>
      <EntityTypeahead
        label="Company"
        placeholder="Search companies..."
        value={companyFilter}
        onSelect={setCompanyFilter}
        onClear={() => setCompanyFilter(null)}
        search={term => Company.list({ search: term, limit: 10 }).then(r => r.data)}
        formatOption={c => c.name}
      />
      <div className="filter-bar__field" style={{ flex: "0 0 auto" }}>
        <span className="filter-bar__label" style={{ visibility: "hidden" }}>&nbsp;</span>
        <div style={{ display: "flex", gap: 10 }}>
          <ButtonGroup>
            <Button onClick={load}>
              Search
            </Button>
          </ButtonGroup>
          <button className="filter-bar__clear" onClick={clearFilters} title="Clear filters">
            <XMarkIcon style={{ width: '16px', height: '16px', display: 'inline-block', verticalAlign: 'middle' }} />
          </button>
        </div>
      </div>
      {loading && (
        <div className="loading-container" style={{ alignSelf: "flex-end", marginBottom: 4 }}>
          <div className="loading-icon"></div>
        </div>
      )}
    </div>
    <div>{data.length} of {total} loaded</div>
      <InfiniteLoader
        isRowLoaded={isRowLoaded}
        loadMoreRows={loadMoreRows}
        rowCount={total}
      >
        {({ onRowsRendered, registerChild }) => (
          <AutoSizer>
            {({ height, width }) => {
              return (
                <Table
                  headerHeight={40}
                  width={width}
                  height={height-120}
                  rowCount={total}
                  rowHeight={45}
                  disableHeader={false}
                  rowGetter={({ index }) => data[index] || {}}
                  onRowsRendered={onRowsRendered}
                  ref={registerChild}
                >
                  <Column
                    label="Name"
                    dataKey="name"
                    width={width / 4}
                    cellRenderer={({ rowData, cellData }) => {
                      if (cellData === undefined) {
                        return "Loading...";
                      }
                      return cellData;
                    }}
                  />
                  <Column
                    label="Company"
                    dataKey="company"
                    width={width / 4}  
                    cellRenderer={({ rowData, cellData }) => {
                      if (cellData === undefined) {
                        return "Loading...";
                      }
                      // console.log(cellData);
                      return cellData.name ? `${cellData.name}` : "";
                    }}
                  />
                  <Column
                    label="Created"
                    dataKey="created_at"
                    width={width / 4}
                    cellRenderer={({ rowData, cellData }) => {
                      if (cellData === undefined) {
                        return "Loading...";
                      }
                      return new Date(cellData).toLocaleString() 
                    }}
                  />
                  <Column
                    label=""
                    dataKey="uuid"
                    width={width / 4}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.uuid) {
                        return "";
                      }
                      return (
                        <ButtonGroup>
                        <Link className='button' to={`/group/${rowData.uuid}/`}>
                          View
                        </Link>
                        </ButtonGroup>
                      );
                    }}
                  />
                </Table>
              );
            }}
          </AutoSizer>
        )}
      </InfiniteLoader>
    </PageLayout>
  );
}
